import { baseApi } from '@api/baseApi';
import { WebOrderStatistics } from '@api/shipping-data/types/WebOrderStatistics';
import { DayCustomersStatistics } from '@api/shipping-data/types/DayCustomersStatistics';
import { ListPage, PagingInfo } from '@api/shipping-data/types/ListPage';
import { TileFilter } from '@api/shipping-data/types/TileFilter';
import { SearchTypes } from '@api/shipping-data/types/SearchTypes';
import { PagingOrdersData } from '@api/shipping-data/types/PagingOrdersData';
import { PagingCustomersData } from '@api/shipping-data/types/PagingCustomersData';



const ordersApi = baseApi.injectEndpoints({
	overrideExisting: false,
	endpoints: (builder) => ({

		// orders
		getOrders: builder.query<PagingOrdersData, { date: string, tileId?: TileFilter, continuationToken?: string }>({
			query: ({ date, tileId, continuationToken }) => ({
				url: 'ShippingDataWeb/GetDayOrderStatsNextPage',
				params: {
					date,
					tileId: tileId ? tileId : TileFilter.totalOrders,
					atLeastRecords: 20
				},
				headers: continuationToken ? { continuationToken } : undefined,
			}),
			serializeQueryArgs: ({ queryArgs }) => ({
				date: queryArgs.date,
				tileId: queryArgs.tileId
			}),
			transformResponse: (returnValue: ListPage<WebOrderStatistics>, _, args) => {
				let newPagingInfo: PagingInfo = {
					continuationToken: returnValue.continuationToken,
					hasNextPage: returnValue.hasNextPage,
					pageItemsCount: returnValue.pageItemsCount ? returnValue.pageItemsCount : 0,
					pageSize: returnValue.pageSize ? returnValue.pageSize : 0,
					totalRecords: returnValue.totalRecords ? returnValue.totalRecords : 0,
					firstPage: args.continuationToken ? false : true,
				};
				return { pagingInfo: newPagingInfo, data: returnValue.pageItems };
			},
			merge: (currentCache, newItems) => {
				if (newItems && newItems.pagingInfo.firstPage) {
					currentCache = newItems;
				}
				else {
					currentCache.pagingInfo = {
						...newItems.pagingInfo,
						pageSize: newItems.pagingInfo.pageSize ? newItems.pagingInfo.pageSize : currentCache.pagingInfo.pageSize,
						pageItemsCount: currentCache.pagingInfo.pageItemsCount + newItems.pagingInfo.pageItemsCount,
						totalRecords: newItems.pagingInfo.totalRecords ? newItems.pagingInfo.totalRecords : currentCache.pagingInfo.totalRecords,
					};
					currentCache.data.push(...newItems.data);
				}
			},
		}),


		// customers
		getCustomers: builder.query<PagingCustomersData, { date: string, continuationToken?: string }>({
			query: ({ date, continuationToken }) => ({
				url: 'ShippingDataWeb/GetDayCustomersStatisticsNextPage',
				params: {
					date,
					atLeastRecords: 20
				},
				headers: continuationToken ? { continuationToken } : undefined,
			}),
			serializeQueryArgs: ({ queryArgs }) => ({
				date: queryArgs.date,
			}),
			transformResponse: (returnValue: ListPage<DayCustomersStatistics>, _, args) => {
				let newPagingInfo: PagingInfo = {
					continuationToken: returnValue.continuationToken,
					hasNextPage: returnValue.hasNextPage,
					pageItemsCount: returnValue.pageItemsCount ? returnValue.pageItemsCount : 0,
					pageSize: returnValue.pageSize ? returnValue.pageSize : 0,
					totalRecords: returnValue.totalRecords ? returnValue.totalRecords : 0,
					firstPage: args.continuationToken ? false : true,
				};
				return { pagingInfo: newPagingInfo, data: returnValue.pageItems };
			},
			merge: (currentCache, newItems) => {
				if (newItems && newItems.pagingInfo.firstPage) {
					currentCache = newItems;
				}
				else {
					currentCache.pagingInfo = {
						...newItems.pagingInfo,
						pageSize: newItems.pagingInfo.pageSize ? newItems.pagingInfo.pageSize : currentCache.pagingInfo.pageSize,
						pageItemsCount: currentCache.pagingInfo.pageItemsCount + newItems.pagingInfo.pageItemsCount,
						totalRecords: newItems.pagingInfo.totalRecords ? newItems.pagingInfo.totalRecords : currentCache.pagingInfo.totalRecords,
					};
					currentCache.data.push(...newItems.data);
				}
			},
		}),


		// fulltext search
		getFulltextSearch: builder.query<PagingOrdersData, { dateFrom: string, dateTo: string, fulltext: string, searchTypes: SearchTypes[] | null, continuationToken?: string }>({
			query: ({ dateFrom, dateTo, fulltext, searchTypes, continuationToken }) => ({
				url: 'ShippingDataWeb/GetFulltextSearchedWebOrderStatisticsNextPage',
				params: {
					dateFrom,
					dateTo,
					fulltext,
					searchTypes: searchTypes ? searchTypes : [ SearchTypes.searchAll ],
					atLeastRecords: 20
				},
				headers: continuationToken ? { continuationToken } : undefined,
			}),
			serializeQueryArgs: ({ queryArgs }) => ({
				dateFrom: queryArgs.dateFrom,
				dateTo: queryArgs.dateTo,
				fulltext: queryArgs.fulltext,
				searchTypes: queryArgs.searchTypes,
			}),
			transformResponse: (returnValue: ListPage<WebOrderStatistics>, _, args) => {
				let newPagingInfo: PagingInfo = {
					continuationToken: returnValue.continuationToken,
					hasNextPage: returnValue.hasNextPage,
					pageItemsCount: returnValue.pageItemsCount ? returnValue.pageItemsCount : 0,
					pageSize: returnValue.pageSize ? returnValue.pageSize : 0,
					totalRecords: returnValue.totalRecords ? returnValue.totalRecords : 0,
					firstPage: args.continuationToken ? false : true,
				};
				return { pagingInfo: newPagingInfo, data: returnValue.pageItems };
			},
			merge: (currentCache, newItems) => {
				if (newItems && newItems.pagingInfo.firstPage) {
					currentCache = newItems;
				}
				else {
					currentCache.pagingInfo = {
						...newItems.pagingInfo,
						pageSize: newItems.pagingInfo.pageSize ? newItems.pagingInfo.pageSize : currentCache.pagingInfo.pageSize,
						pageItemsCount: currentCache.pagingInfo.pageItemsCount + newItems.pagingInfo.pageItemsCount,
						totalRecords: newItems.pagingInfo.totalRecords ? newItems.pagingInfo.totalRecords : currentCache.pagingInfo.totalRecords,
					};
					currentCache.data.push(...newItems.data);
				}
			},
			forceRefetch: ({ currentArg }) => currentArg && !currentArg.continuationToken ? true : false,
		}),


		// single order
		getSingleOrder: builder.query<WebOrderStatistics, string>({
			query: (unversionedSystemId) => ({
				url: 'ShippingDataWeb/GetDayWebOrderStatistics',
				params: { unversionedSystemId },
			}),
			providesTags: ['singleOrder'],
		}),

		resetSingleOrder: builder.mutation<null, void>({
			queryFn: () => ({ data: null }),
			invalidatesTags: ['singleOrder'],
		}),

	}),
});


export const {
	// orders
	useLazyGetOrdersQuery,
	// customers
	useLazyGetCustomersQuery,
	// fulltext search
	useLazyGetFulltextSearchQuery,
	// single order
	useLazyGetSingleOrderQuery,
	useResetSingleOrderMutation } = ordersApi;